import { Box, Chip, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

import {
	BaseNodeBody,
	BaseNodeContainer,
	BaseNodeLabelContainer,
	BaseNodeMainContent,
} from "../styled/basenode.styled";

// Shared card for the loop sub-canvas sentinels (Loop Start / Continue / End).
// Uses the SAME BaseNode styled building blocks as every main-canvas node so the
// sentinels read as the same design language: a tinted header strip with the
// node's icon (glyph painted in its brand gradient) + an uppercase type label,
// then a white body with a bold underlined title, a small status badge, and a
// description. Handles are passed through as `children` so each sentinel keeps
// its exact wiring.
type LoopSentinelCardProps = {
	icon: ReactNode;
	/** Accent color — drives the tint strip, border, label, badge. */
	accentColor: string;
	/** id of a mounted <linearGradient> (CoAppGradientDefs) for the glyph fill. */
	gradId: string;
	label: string;
	title: string;
	badge: string;
	description: string;
	selected?: boolean;
	/** React Flow <Handle> elements. */
	children?: ReactNode;
};

export default function LoopSentinelCard({
	icon,
	accentColor,
	gradId,
	label,
	title,
	badge,
	description,
	selected,
	children,
}: LoopSentinelCardProps) {
	return (
		<BaseNodeContainer sx={{ width: 250 }}>
			<BaseNodeMainContent elevation={0} selected={selected} accentColor={accentColor}>
				<BaseNodeLabelContainer accentColor={accentColor}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 26,
							height: 26,
							borderRadius: 1.25,
							// Subtle tint tile in the accent color, glyph painted with
							// the matching brand gradient (mirrors BaseNode + the form
							// header "formula variable" style).
							background: `${accentColor}24`,
							"& > *": { fontSize: "16px !important" },
							"& path": { fill: `url(#${gradId})` },
						}}
					>
						{icon}
					</Box>
					<Typography variant="caption" noWrap sx={{ color: `${accentColor} !important` }}>
						{label}
					</Typography>
				</BaseNodeLabelContainer>

				<BaseNodeBody>
					<Stack spacing={0.5}>
						<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
							<Typography
								variant="subtitle1"
								sx={{
									fontWeight: 800,
									fontSize: 17,
									letterSpacing: "-0.01em",
									color: "rgba(13, 31, 48, 0.92)",
									lineHeight: 1.3,
									textDecoration: "underline",
									textDecorationThickness: "1px",
									textUnderlineOffset: "3px",
									textDecorationColor: "rgba(13, 31, 48, 0.25)",
								}}
							>
								{title}
							</Typography>
							<Chip
								label={badge}
								size="small"
								sx={{
									height: 18,
									fontSize: "0.65rem",
									bgcolor: `${accentColor}22`,
									color: accentColor,
									fontWeight: 600,
								}}
							/>
						</Stack>
						<Typography variant="body2" color="text.secondary">
							{description}
						</Typography>
					</Stack>
				</BaseNodeBody>
			</BaseNodeMainContent>

			{children}
		</BaseNodeContainer>
	);
}
